// src/modules/app-views/account/pages/Subscription/modals/CertificateUploadModal/Step1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect } from 'react';
import { Upload, Typography, Space } from 'antd';
import { InboxOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import SimplePassword from 'components/SimpleComponents/SimplePassword';
import appService from 'services/appService';
import { parseCertificate } from './certificateParser';

const { Dragger } = Upload;
const { Text } = Typography;

// *******************************************************************************
// HELPERS
// *******************************************************************************
const formatFileSize = bytes => {
  if (!bytes) return '';
  const kb = bytes / 1024;
  return kb < 1 ? `${bytes}B` : `${kb.toFixed(1)}KB`;
};

/**
 * Normalize CNPJ for comparison (remove all non-numeric characters)
 */
const normalizeCnpj = cnpj => {
  if (!cnpj) return '';
  return String(cnpj).replace(/\D/g, '');
};

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const UploadStepContent = () => {
  const { form } = useSimpleFormContext();

  // Local state for file list display
  const [fileList, setFileList] = useState([]);

  // Sync fileList with form value on mount
  useEffect(() => {
    const certificateFile = form.getFieldValue('certificateFile');
    if (certificateFile?.name) {
      setFileList([
        {
          uid: '-1',
          name: `${certificateFile.name} (${formatFileSize(certificateFile.size)})`,
          status: 'done',
        },
      ]);
    }
  }, [form]);

  const handleBeforeUpload = file => {
    // Validate file extension
    const validExtensions = ['.pfx', '.p12'];
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));

    if (!validExtensions.includes(fileExtension)) {
      appService.message({ flag: 'e', text: 'invalid_certificate_format' });
      return Upload.LIST_IGNORE;
    }

    // Validate file size (max 50KB - certificates are typically small)
    const maxSize = 50 * 1024; // 50KB
    if (file.size > maxSize) {
      appService.message({ flag: 'e', text: 'certificate_file_too_large' });
      return Upload.LIST_IGNORE;
    }

    // Read file as base64 and store in form
    const reader = new FileReader();
    reader.onload = e => {
      const base64 = e.target.result.split(',')[1]; // Remove data:... prefix
      form.setFieldsValue({
        certificateFile: {
          name: file.name,
          base64: base64,
          size: file.size,
        },
        // Clear password when new file is uploaded
        certificatePassword: '',
      });

      // Update local file list to show the uploaded file
      setFileList([
        {
          uid: '-1',
          name: `${file.name} (${formatFileSize(file.size)})`,
          status: 'done',
        },
      ]);

      // Auto-focus password field after file is selected
      setTimeout(() => {
        const passwordInput = document.querySelector('input[id="certificatePassword"]');
        passwordInput?.focus();
      }, 150);
    };
    reader.readAsDataURL(file);

    // Prevent auto upload
    return false;
  };

  const handleRemove = () => {
    form.setFieldsValue({
      certificateFile: null,
      certificatePassword: '',
    });
    setFileList([]);
  };

  // Common upload props
  const uploadProps = {
    name: 'certificate',
    multiple: false,
    accept: '.pfx,.p12',
    maxCount: 1,
    beforeUpload: handleBeforeUpload,
    onRemove: handleRemove,
    fileList: fileList,
    listType: 'picture',
    iconRender: () => <SafetyCertificateOutlined style={{ fontSize: 24, color: '#52c41a' }} />,
  };

  const hasFile = fileList.length > 0;

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="middle">
      {/* Show Dragger only when no file is uploaded */}
      {!hasFile && (
        <Dragger {...uploadProps} style={{ padding: '20px 0' }}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">{translateX('click_or_drag_certificate')}</p>
          <p className="ant-upload-hint">
            <Text type="secondary">{translateX('supported_formats')}: .pfx, .p12</Text>
          </p>
        </Dragger>
      )}

      {/* Show file list + password when file is uploaded */}
      {hasFile && (
        <>
          <Upload {...uploadProps}>
            <span />
          </Upload>

          <SimplePassword
            name="certificatePassword"
            label="certificate_password"
            required
            autoComplete="off"
            placeholder="••••••••"
          />
        </>
      )}
    </Space>
  );
};

// *******************************************************************************
// VALIDATION
// *******************************************************************************
const validateCertificateAndPassword = async ({ form, setLoading, goFocus }) => {
  const certificateFile = form.getFieldValue('certificateFile');
  const password = form.getFieldValue('certificatePassword');
  const companyCnpj = form.getFieldValue('companyCnpj');

  // Validate file is uploaded
  if (!certificateFile || !certificateFile.base64) {
    await appService.message({ flag: 'w', text: 'please_select_certificate' });
    return false;
  }

  // Validate password is filled
  if (!password || password.trim() === '') {
    await appService.message({ flag: 'w', text: 'password_is_required' });
    goFocus?.('certificatePassword', true);
    return false;
  }

  try {
    setLoading(true);

    // Parse certificate with password to extract info
    const certInfo = await parseCertificate(certificateFile.base64, password);

    if (!certInfo.success) {
      await appService.message({
        flag: 'e',
        text: certInfo.error || 'invalid_certificate_password',
      });
      goFocus?.('certificatePassword', true);
      return false;
    }

    // Validate CNPJ match
    const certCnpj = normalizeCnpj(certInfo.data?.cnpj);
    const entityCnpj = normalizeCnpj(companyCnpj);

    if (certCnpj && entityCnpj && certCnpj !== entityCnpj) {
      appService.notification({
        flag: 'warning',
        title: 'certificate_cnpj_mismatch_title',
        description: 'certificate_cnpj_mismatch_description',
        duration: 8,
      });
      goFocus?.('certificatePassword', true);
      return false;
    }

    // Store parsed certificate info in form for Step 2 (summary)
    form.setFieldsValue({
      certificateInfo: certInfo.data,
    });

    return true;
  } catch (error) {
    console.error('[CertificateUploadModal] Parse error:', error);
    await appService.message({ flag: 'e', text: 'certificate_parse_error' });
    goFocus?.('certificatePassword', true);
    return false;
  } finally {
    setLoading(false);
  }
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const uploadStep = () => ({
  stepTitle: 'upload',
  stepTooltip: 'upload_certificate',
  content: () => <UploadStepContent />,
  validationFields: ['certificatePassword'],
  onNext: validateCertificateAndPassword,
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default uploadStep;
